import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import { RouteComponentProps } from "react-router";
import { withRouter } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import AddIcon from "@material-ui/icons/Add";
import { Button } from "@material-ui/core";

import LayoutContainer from "components/Layout/Container";
import Header from "components/Layout/Header";
import PublishButton from "components/PublishButton";
import Page from "components/Layout/Page";
import Placeholder from "components/Placeholder";
import FilteringControl from "components/FilteringControl";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { SNACKBAR_TYPES } from "components/Snackbar";
import { TableSortType } from "stores/templates/TableStore";

import {
  PageControl,
  OverflowLoader,
  ScrollLoader,
  CardWrapper,
  FilterContainer,
} from "./styles";

import GreetingCard from "./GreetingCard";
import { constants } from "./constants";
import GreetingStore from "stores/GreetingStore";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import { areStatusFiltersEmpty } from "utils/filter";
import ConfirmationDialog from "components/ConfirmationDialog";
import GreetingForm from "./GreetingForm";

type Props = RouteComponentProps & GlobalPopupProps & SnackbarContextProps & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class Greetings extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;
  @computed get items() {
    return GreetingStore.items;
  }

  @computed get isListEmpty() {
    return this.items.length === 0;
  }

  componentDidMount() {
    GreetingStore.fetch();
  }

  componentWillUnmount() {
    GreetingStore.resetPage();
  }

  fetchMoreItems = async () => {
    const { setMessage } = this.props;

    try {
      await GreetingStore.fetchMore();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    GreetingStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH && GreetingStore.fetch();
  };

  onSearchClear = () => {
    GreetingStore.clearSearch();
    GreetingStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    GreetingStore.applyOrder(value);
    GreetingStore.resetPage();
    GreetingStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      GreetingStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    GreetingStore.resetPage();
    GreetingStore.fetch();
  };

  onDragEnd = async (result: DropResult) => {
    const { setMessage } = this.props;

    // dropped outside the list
    if (
      !result.destination ||
      !result.source ||
      result.source.index === result.destination.index
    ) {
      return;
    }

    const startIndex = result.source.index;
    const endIndex = result.destination.index;

    try {
      await GreetingStore.reorderCategories({ startIndex, endIndex });
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onFilterClear = () => {
    GreetingStore.clearFilters();
    GreetingStore.fetch();
  };

  onAddItemClick = () => {
    this.onManageGreeting();
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  shouldShowConfirmation = () => {
    return this.isPopupDirty;
  };

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  onManageGreeting = (initialValues?: any) => {
    const { openPopup } = this.props;

    openPopup({
      key: "ManageGreeting",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      confirmation: {
        renderConfirmation: ({ deny, allow }) => (
          <ConfirmationDialog
            title="Discard Changes"
            text="Are you sure you would like to discard changes? All information will be lost."
            declineText="Cancel"
            acceptText="Discard"
            onDecline={deny}
            onAccept={allow}
          />
        ),
        shouldShowConfirmation: this.shouldShowConfirmation,
      },
      render: ({ close }) => (
        <GreetingForm
          isLoading={GreetingStore.isGreetingManaging}
          isFormLoading={GreetingStore.isExporting}
          initialValues={initialValues}
          onSubmit={this.onManageGreetingSubmit(close)}
          onClose={close}
          onChangeIsDirty={this.onChangeIsPopupDirty}
        />
      ),
    });
  };

  onManageGreetingSubmit = (close: () => void) => async (values: any) => {
    const { setMessage } = this.props;
    const { file, id } = values;

    const payload = { file, id };

    try {
      if (!id) {
        await GreetingStore.createGreeting(payload);
      } else {
        if (typeof file !== "string") {
          const payload = { file, id };
          await GreetingStore.editGreetingInfo(payload);
        }
      }

      this.resetIsPopupDirty();
      close();
      GreetingStore.resetPage();
      GreetingStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onEdit = (item: any) => () => {
    const id = item.id;

    this.onManageGreeting({ id });
  };

  onPublish = (item: any) => async () => {
    const { setMessage } = this.props;
    const { id, publishStatus } = item;

    try {
      await GreetingStore.changeStatus(id, publishStatus);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  @computed get isFiltersEmpty() {
    const { filters } = GreetingStore;

    return areStatusFiltersEmpty(filters);
  }

  @computed get isOverflowLoaderShown() {
    return (
      GreetingStore.isGreetingReordering || GreetingStore.isGreetingPublishing
    );
  }

  render() {
    return (
      <LayoutContainer header={<Header controls={<PublishButton />} />}>
        <Page
          title={constants.greetings.title}
          controls={
            <>
              <PageControl>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={this.onAddItemClick}
                >
                  {constants.greetings.addButtonTitle}
                </Button>
              </PageControl>
            </>
          }
          isLoading={GreetingStore.isLoading}
        >
          <FilterContainer>
            <FilteringControl
              initValue={GreetingStore.filters}
              onApply={this.onFilterApply}
              onClear={this.onFilterClear}
              statusFiltering
            />
          </FilterContainer>

          {this.isOverflowLoaderShown && <OverflowLoader />}
          {!this.isListEmpty ? (
            <InfiniteScroll
              dataLength={this.items.length}
              hasMore={GreetingStore.hasMoreInfo}
              next={this.fetchMoreItems}
              height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
              loader={<ScrollLoader />}
            >
              <DragDropContext onDragEnd={this.onDragEnd}>
                <Droppable droppableId="droppable">
                  {(provided, snapshot) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {this.items.map((item, index) => (
                        <Draggable
                          key={item.id}
                          draggableId={item.id.toString()}
                          index={index}
                          isDragDisabled={!this.isFiltersEmpty}
                        >
                          {(provided, snapshot) => (
                            <CardWrapper
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                            >
                              <GreetingCard
                                image={item.url}
                                publishStatus={item.publishStatus}
                                isDragging={snapshot.isDragging}
                                onEdit={this.onEdit(item)}
                                onPublish={this.onPublish(item)}
                                dragHandleProps={provided.dragHandleProps}
                              />
                            </CardWrapper>
                          )}
                        </Draggable>
                      ))}

                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </InfiniteScroll>
          ) : (
            <Placeholder
              height={280}
              width={280}
              text={
                GreetingStore.search
                  ? constants.greetings.noRecordsMatch
                  : constants.greetings.noRecords
              }
            />
          )}
        </Page>
      </LayoutContainer>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(Greetings)));
